import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UNSAFE_ErrorResponseImpl } from 'react-router';

import { VButton, VFlex, VStack } from '@/package';
import { actionHistory, axiosActionHistory, exportException } from '@/util';

type ErrorFallbackProps<Err extends Error | UNSAFE_ErrorResponseImpl> = {
  code?: number;
  error: Err;
  info?: Nullish<{ componentStack: string }>;
  onRefresh?: () => any;
};

const ErrorFallback = <Err extends Error | UNSAFE_ErrorResponseImpl>({
  code,
  error,
  info,
  onRefresh,
}: ErrorFallbackProps<Err>) => {
  console.warn(error);
  const message =
    error instanceof UNSAFE_ErrorResponseImpl ? error.data : error.message;
  const { t } = useTranslation();

  useEffect(() => {
    exportException.report({
      message,
      stack:
        info?.componentStack ?? (error instanceof Error ? error.stack : null),
      pathname: window.location.pathname,
      datetime: new Date().toISOString(),
      appVersion: import.meta.env.VITE_APP_VERSION,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      actionLog: {
        actionHistory: actionHistory.actionHistory,
        axiosActionHistory: axiosActionHistory.actionHistory,
      },
    });
  }, [code, error, info]);

  return (
    <>
      <VStack
        className="relative z-1 h-screen w-screen"
        justify="center"
        items="center"
        gap={1}
      >
        <VFlex
          className="size-[325px] bg-[url(/oops.png)] bg-contain @md:size-[400px]"
          items="center"
          justify="center"
        >
          <div className="text-[52px] font-bold text-blue-400/40">{code}</div>
        </VFlex>
        <VStack gap={0.25}>
          {code === 404 && (
            <div className="text-center text-blue-300">
              Oops! There’s nothing here!
            </div>
          )}
          <div className="text-center text-headline-lg font-bold whitespace-pre-line text-primary-500">
            {code === 404 ? t('errorPage.404Message.hippomancy') : message}
          </div>
        </VStack>
        <VButton size="lg" onClick={() => onRefresh?.()}>
          {t('common.refresh.framea')}
        </VButton>
      </VStack>
    </>
  );
};

export default ErrorFallback;
